import { useEffect } from 'react';
import type { SortEnd } from 'react-sortable-hoc';
import Scrollbars from 'react-custom-scrollbars';
import { delay } from 'pano-utils';
import type { ComponentLayer, SceneData } from '@types';
import SidebarHeader from '@modules/sidebar/components/SidebarHeader';
import Title from '@components/Title';
import Tip from '@components/Tip';
import { TipWrapper } from '@components/TipWrapper';
import IconBatch from '@components/icons/IconBatch';
import IconVip from '@components/icons/IconVip';
import Button from '@components/Button';
import useToggle from '@hooks/useToggle';
import { $sceneMapApi } from '@stores/dataStores';
import arrayMove from '@utils/arrayMove';
import SortableList from '@components/SortableList';
import {
  $editingComponentLayer,
  $editingComponentLayerApi,
} from '@stores/editingComponentLayerStores';
import SidebarApplyTo from '@modules/sidebar/components/SidebarApplyTo';
import { showApplyWarning, showConfirmModal } from '@components/ConfirmModal';
import { showSceneModal } from '@modules/category';
import { sendSuccess } from '@components/Noti';
import { showVipModal } from '@utils/showVipModal';
import { C_LAYER_DEFAULT, C_LAYER_MAX } from '../constants';
import { generateCLayerId, renewCLayerId } from '../utils/generateCLayerId';
import ComponentLayerItem from './ComponentLayerItem';
import { onComponentLayerClick } from '../actions';

interface Props {
  scene: SceneData;
  onBatchClick: () => void;
}

export function ComponentLayerList({ scene, onBatchClick }: Props) {
  const list = scene.config.components ?? [];
  const isMax = list.length >= C_LAYER_MAX;
  const loading = useToggle(false);

  useEffect(() => {
    const unwatch = onComponentLayerClick.watch(async ({ id }) => {
      const editing = $editingComponentLayer.getState();
      if (editing?.layer.id === id) return;
      const item = list.find((x) => x.id === id);
      if (!item) return;
      if (editing?.layer.id) {
        const oldData = list.find((x) => x.id === editing.layer.id);
        if (editing?.layer !== oldData || editing?.isNew) {
          const { confirmed } = await showConfirmModal({
            title: '有未保存的更改',
            content: '当前所有未经保存的改动将丢失, 是否继续?',
          });
          if (!confirmed) return;
        }
        $editingComponentLayerApi.clear();
        await delay(100);
      }
      $editingComponentLayerApi.set({
        layer: {
          ...item,
          animation: item.animation ?? C_LAYER_DEFAULT.animation,
          enterAnimation: item.enterAnimation ?? C_LAYER_DEFAULT.enterAnimation,
          exitAnimation: item.exitAnimation ?? C_LAYER_DEFAULT.exitAnimation,
          hideInClearMode:
            item.hideInClearMode ?? C_LAYER_DEFAULT.hideInClearMode,
        },
        sceneId: scene.id,
      });
    });
    return () => {
      unwatch();
    };
  }, [list]);

  const handleAddClick = () => {
    showVipModal('720_vip_component_layer');
    loading.on();
    const newItem: Required<ComponentLayer> = {
      ...C_LAYER_DEFAULT,
      id: generateCLayerId(),
      zIndex: list.length,
    };
    $editingComponentLayerApi.set({
      sceneId: scene.id,
      layer: newItem,
      isNew: true,
    });
    loading.off();
  };

  const handleSortEnd = (sort: SortEnd) => {
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        draft.config.components ??= [];
        draft.config.components = arrayMove({
          array: draft.config.components,
          fromIndex: sort.oldIndex,
          toIndex: sort.newIndex,
        }).map((x, i) => ({ ...x, zIndex: i }));
      },
    });
  };

  const handleApplyTo = async () => {
    const components = scene.config.components ?? [];
    if (!components.length) {
      const { confirmed } = await showApplyWarning();
      if (!confirmed) return;
    }
    const { idSet } = await showSceneModal({
      title: '选择要应用到的场景',
      multiple: true,
    });
    if (!idSet) return;
    $sceneMapApi.update((draft) => {
      Array.from(idSet).forEach((_id) => {
        if (_id !== scene.id) {
          draft.get(_id)!.config.components = renewCLayerId(components);
        }
      });
    });
    sendSuccess('图层设置已应用到所选场景');
  };

  const renderList = () => {
    return (
      <div className="relative flex-1">
        <SortableList
          axis="y"
          lockAxis="y"
          distance={10}
          onSortEnd={handleSortEnd}
        >
          <Scrollbars>
            {list.map((x, i) => (
              <ComponentLayerItem
                key={x.id}
                data={x}
                sceneId={scene.id}
                index={i}
              />
            ))}
          </Scrollbars>
        </SortableList>
      </div>
    );
  };

  const renderEmpty = () => {
    return (
      <div className="flex-1">
        <Tip className="mt-5 text-center text-base">
          {`每个场景最多可添加${C_LAYER_MAX}个图层`}
        </Tip>
        <Tip className="mt-5 text-center text-base">
          添加后支持上下拖拽调整图层顺序
        </Tip>
      </div>
    );
  };

  return (
    <>
      <SidebarHeader
        title={
          <div className="flex items-center">
            <Title>图层设置</Title>
            <IconVip className="ml-5" />
            <Tip className="ml-5 text-base">
              {`${list.length}/${C_LAYER_MAX}`}
            </Tip>
          </div>
        }
        btns={
          <TipWrapper text="批量操作" placement="bottom" size="sm">
            <IconBatch className="focus:outline-none" onClick={onBatchClick} />
          </TipWrapper>
        }
      />
      <Button
        size="lg"
        className="mb-10 w-full"
        disabled={loading.isOn || isMax}
        onClick={handleAddClick}
      >
        添加图层
      </Button>
      {list.length > 0 ? renderList() : renderEmpty()}
      <SidebarApplyTo
        className="mt-10"
        title="把图层设置应用到..."
        onClick={handleApplyTo}
      />
    </>
  );
}

export default ComponentLayerList;
